import React, { useState } from 'react';
import { Client } from 'colyseus.js';

function ColyseusMultiplayerPage() {
    const [isConnected, setIsConnected] = useState(false);
    const [isConnecting, setIsConnecting] = useState(false);
    const [error, setError] = useState(null);
    const [client, setClient] = useState(null);
    const [room, setRoom] = useState(null);
    const [gameActive, setGameActive] = useState(false);
    const [currentQuestion, setCurrentQuestion] = useState(null);
    const [questionType, setQuestionType] = useState('');
    const [options, setOptions] = useState({});
    const [timeRemaining, setTimeRemaining] = useState(10);
    const [questionNumber, setQuestionNumber] = useState(0);
    const [totalQuestions, setTotalQuestions] = useState(5);
    const [answer, setAnswer] = useState('');
    const [result, setResult] = useState(null);
    const [players, setPlayers] = useState([]);
    const [token, setToken] = useState(() => localStorage.getItem('authToken') || '');
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    const [questionActive, setQuestionActive] = useState(false);

    // For score change
    const [scoreChanges, setScoreChanges] = useState({});

    // For previous player states
    const [previousPlayerStates, setPreviousPlayerStates] = useState([]);
    
    const connect = async () => {
        // initialize or generate auth token
        let authToken = token;
        if (!authToken) {
            authToken = Math.random().toString(36).substring(2);
            setToken(authToken);
            localStorage.setItem('authToken', authToken);
        }
        if (isConnecting || isConnected) return;
        setIsConnecting(true);
        setError(null);

        try {
            const colyseusClient = new Client(process.env.REACT_APP_COLYSEUS_WS_URL || 'wss://api.gameyoursat.com/ws');
            setClient(colyseusClient);
            const gameRoom = await colyseusClient.joinOrCreate('question_answer', { token: authToken });
            setRoom(gameRoom);
        // sync game status if joining mid-game, disable start and await next question
        setGameActive(gameRoom.state.gameActive);
            // listen for authentication confirmation from server
            gameRoom.onMessage('authenticated', (message) => {
                console.log('Authenticated message received from server:', message);
                setIsAuthenticated(!!message.token);
            });

            gameRoom.onStateChange((state) => {
                        
                const livePlayers = Array.from(state.players.entries()).map(([id, player]) => ({
                    id,
                    name: player.name,
                    score: player.score,
                    correctAnswers: player.correctAnswers,
                    totalAnswers: player.totalAnswers,
                    accuracy: player.totalAnswers > 0 ? (player.correctAnswers / player.totalAnswers * 100).toFixed(1) : '0'
                }));
                        
                setPlayers(livePlayers);
            });

            gameRoom.onMessage('gameStarted', () => {
    setGameActive(true);
    setResult(null);

    // Reset players scores to zero on new game start
    if (room && room.state && room.state.players) {
        const resetPlayers = Array.from(room.state.players.entries()).map(([id, player]) => ({
            id,
            name: player.name,
            score: 0,
            correctAnswers: 0,
            totalAnswers: 0,
            accuracy: '0'
        }));
        setPlayers(resetPlayers);
    } else {
        setPlayers([]);
    }
});

            gameRoom.onMessage('newQuestion', (data) => {
                // Used if we decide to show ranking update when question is not active
                setQuestionActive(true);
                // Reset scoreChanges to +0 at start of new question
                setScoreChanges({});

                // previous ranking and player states 
                const snapshot = Array.from(gameRoom.state.players.entries()).map(([id, player]) => ({
                    id,
                    name: player.name,
                    score: player.score,
                    correctAnswers: player.correctAnswers,
                    totalAnswers: player.totalAnswers,
                    accuracy: player.totalAnswers > 0 ? (player.correctAnswers / player.totalAnswers * 100).toFixed(1) : '0'
                }));
                const sortedIds = [...snapshot].sort((a, b) => b.score - a.score).map(p => p.id);

                setPreviousPlayerStates(snapshot);

                setCurrentQuestion(data.question);
                setQuestionType(data.questionType);
                setOptions({
                    A: data.optionA, B: data.optionB, C: data.optionC, D: data.optionD
                });
                setQuestionNumber(data.questionNumber);
                setTotalQuestions(data.totalQuestions);
                setTimeRemaining(data.timeLimit);
                setAnswer('');
                setResult(null);
            });

            gameRoom.onMessage('timeUpdate', (data) => {
                setTimeRemaining(data.remaining);
            });

            gameRoom.onMessage('answerResult', (data) => {
                setResult({
                    correct: data.correct,
                    points: data.points,
                    correctAnswer: data.correctAnswer,
                    feedback: data.feedback
                });
            });

            // Used to update the score change for the question, broadcast to everyone
            gameRoom.onMessage('scoreChange', (data) => {
                const{playerId, delta, isCorrect} = data;

                // delta is the change between points
                setScoreChanges(prev => ({
                    ...prev, [playerId]: {
                        delta: Math.abs(delta),
                        isCorrect
                    }
                }))
            })


            gameRoom.onMessage('questionEnded', () => {
                // Used if we decide to show ranking update when question is not active
                setQuestionActive(false);

                const sortedNow = [...players].sort((a, b) => b.score - a.score).map(p => p.id);
                const changes = {};
                
                setPreviousPlayerStates(players => [...players])

                setCurrentQuestion(null);
                setTimeRemaining(0);
            });

            gameRoom.onMessage('gameEnded', (data) => {
                setGameActive(false);
                setCurrentQuestion(null);
            });

            gameRoom.onLeave(() => {
                setIsConnected(false);
                setRoom(null);
                setGameActive(false);
            });

            gameRoom.onError((code, message) => {
                setError(`Room error: ${message}`);
            });

            setIsConnected(true);
        } catch (err) {
            setError(err.message || 'Failed to connect');
        } finally {
            setIsConnecting(false);
        }
    };

    const disconnect = () => {
        if (room) room.leave();
        if (client) client.close();
        setClient(null);
        setRoom(null);
        setIsConnected(false);
        setGameActive(false);
        setError(null);
    };

    const startGame = () => {
        if (room && isConnected) {
            room.send('startGame');
        }
    };

    const submitAnswer = () => {
        if (room && isConnected && answer.trim() !== '') {
            room.send('submitAnswer', { answer: answer });
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && answer.trim() !== '') {
            submitAnswer();
        }
    };

    return (
        <div className="colyseus-multiplayer-page min-h-screen w-full ml-24 p-6 flex flex-col items-center font-zonaProRegular">
            <div className="w-full max-w-4xl game-card p-6">
                <div className="text-center mb-4">
                    <h1 className="text-4xl font-zonaProUltra text-">Questions & Answers</h1>
                    <div className="neon-divider my-3"/>
                    <p className="text-black/80">Answer 5 basic math questions</p>
                </div>

                <div className="text-sm grid grid-cols-1 md:grid-cols-3 gap-2 mb-6">
                    <div className={`p-2 rounded text-center border ${isConnected ? 'bg-green-600/20 text-green-200 border-green-400/50' : 'bg-red-600/20 text-red-200 border-red-400/50'}`}>
                        {isConnected ? 'Connected' : 'Disconnected'}
                    </div>
                    <div className={`p-2 rounded text-center border ${isConnecting ? 'bg-yellow-500/20 text-yellow-200 border-yellow-400/50' : 'bg-black/5 text-black/70 border-black/10'}`}>{isConnecting ? '🔄 Connecting…' : 'Idle'}</div>
                    <div className="p-2 rounded text-center border border-black/10 bg-black/5 text-black/80">Auth: {isAuthenticated ? 'Yes' : 'No'} • Token: {token}</div>
                    {error && (
                        <div className="md:col-span-3 p-2 rounded text-center bg-red-600/20 text-red-200 border border-red-400/50">Error: {error}</div>
                    )}
                </div>

                <div className="mb-6 flex flex-wrap gap-3">
                    {!isConnected && !isConnecting && (
                        <button onClick={connect} className="game-btn game-btn--primary">Connect to Server</button>
                    )}
                    {isConnected && !gameActive && (
                        <button onClick={startGame} className="game-btn game-btn--primary">Start Quiz Game</button>
                    )}
                </div>

                {gameActive && currentQuestion && (
                    <div>
                        <div className="text-black/90 font-semibold">{`Question ${questionNumber} of ${totalQuestions}`}</div>
                        <div className="text-black/70 mb-2">{`Time remaining: ${timeRemaining}s`}</div>
                        <div className="text-lg my-4 p-4 bg-slate-900/60 border border-black/10 rounded text-black shadow-sm">{currentQuestion}</div>
                        {questionType === 'Multiple Choice' ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 my-4">
                                {Object.entries(options).map(([key, value]) =>
                                    value && (
                                        <button
                                            key={key}
                                            className={`px-4 py-3 rounded-lg border transition-colors duration-150 text-left ${answer === value ? 'text-black' : 'bg-black/5 border-black/10 text-black/90 hover:bg-black/10'}`}
                                            style={answer === value ? { backgroundColor: '#34c759', borderColor: '#34c759' } : {}}
                                            onClick={() => setAnswer(value)}
                                            disabled={result !== null}
                                        >
                                            {`${key}. ${value}`}
                                        </button>
                                    )
                                )}
                            </div>
                        ) : (
                            <input
                                type="text"
                                placeholder="Enter your answer..."
                                value={answer}
                                onChange={(e) => setAnswer(e.target.value)}
                                onKeyPress={handleKeyPress}
                                disabled={result !== null}
                                className="w-full p-3 rounded-lg bg-transparent border border-black/20 text-black placeholder-black/60 focus:outline-none focus:ring-2 focus:ring-[var(--sat-neon)]"
                            />
                        )}
                        {!result && (
                            <button onClick={submitAnswer} disabled={answer.trim() === ''} className="mt-2 py-3 px-6 text-black font-zonaProUltra rounded-full transition disabled:opacity-60 disabled:cursor-not-allowed" style={{ backgroundColor: '#34c759' }}>
                                Submit Answer
                            </button>
                        )}
                    </div>
                )}

                {result && (
                    <div className={`p-3 rounded font-bold mt-4 ${result.correct ? 'bg-green-950/40 text-green-200 border border-green-400/40' : 'bg-red-950/40 text-red-200 border border-red-400/40'}`}>
                        {result.correct
                ? `CORRECT: ${result.feedback}`
                : questionType === 'Multiple Choice'
                    ? `WRONG: The correct answer was "${result.correctAnswer}", ${result.feedback}`
                            : `WRONG: The correct answer was ${result.correctAnswer}, ${result.feedback}`}
                    </div>
                )}

                {questionActive && questionNumber === 1 ? (
                    <div className="mt-6 p-4 bg-slate-900/60 border border-black/10 rounded">
                        <h3 className="text-black text-xl font-zonaProUltra mb-2">Question Scoreboard</h3>
                        {previousPlayerStates.length === 0 ? (
                            <p className="text-black/80">No players connected</p>
                        ) : (
                            previousPlayerStates
                        .sort((a, b) => b.score - a.score)
                                .map((player, index) => (
                                    <div key={player.id} className="flex justify-between py-2 border-b border-black/10 text-black/90">
                                        <span>{`${index + 1}. ${player.name}`}</span>
                                        <span>0 pts</span>
                                    </div>
                                ))
                        )}
                    </div>
                ) : questionActive ? (
                    <div className="mt-6 p-4 bg-slate-900/60 border border-black/10 rounded">
                        <h3 className="text-black text-xl font-zonaProUltra mb-2">Question Scoreboard</h3>
                        {previousPlayerStates.length === 0 ? (
                            <p className="text-black/80">No players connected</p>
                        ) : (
                            previousPlayerStates
                        .sort((a, b) => b.score - a.score)
                                .map((player, index) => (
                                    <div key={player.id} className="flex justify-between py-2 border-b border-black/10 text-black/90">
                                        <span>{`${index + 1}. ${player.name}`}</span>
                                        <span>{`${player.score} pts`}</span>
                                    </div>
                                ))
                        )}
                    </div>
                ) : gameActive ? (
                    <div className="mt-6 p-4 bg-slate-900/60 border border-black/10 rounded">
                        <h3 className="text-black text-xl font-zonaProUltra mb-2">Results Scoreboard</h3>
                        {players.length === 0 ? (
                            <p className="text-black/80">No players connected</p>
                        ) : (
                            players
                        .sort((a, b) => b.score - a.score)
                                .map((player, index) => (
                                    <div key={player.id} className="flex justify-between py-2 border-b border-black/10 text-black/90">
                                        <span>
                                            {`${index + 1}. ${player.name}`}
                                            <span
                                                className="ml-8"
                                                style={{
                                                    color: scoreChanges[player.id]
                                                        ? scoreChanges[player.id].isCorrect
                                                            ? 'rgb(74 222 128)' // green-400
                                                            : scoreChanges[player.id].delta === 0
                                                            ? 'rgb(148 163 184)' // slate-400
                                                            : 'rgb(248 113 113)' // red-400
                                                        : 'rgb(148 163 184)',
                                                }}
                                            >
                                                {scoreChanges[player.id]
                                                    ? scoreChanges[player.id].delta === 0
                                                        ? '+0'
                                                        : `${scoreChanges[player.id].isCorrect ? '+' : '-'}${Math.abs(scoreChanges[player.id].delta)}`
                                                    : '+0'}
                                            </span>
                                        </span>
                                        <span>
                                            {player.totalAnswers > 0
                                            ? `${player.score} pts (${player.accuracy}% accuracy)`
                                                : `${player.score} pts`}
                                        </span>
                                    </div>
                                ))
                        )}
                    </div>
                ) : (
                    <div className="mt-6 p-4 bg-slate-900/60 border border-black/10 rounded">
                        <h3 className="text-black text-xl font-zonaProUltra mb-2">Player List</h3>
                        {players.length === 0 ? (
                            <p className="text-black/80">No players connected</p>
                        ) : (
                            players
                    .sort((a, b) => b.score - a.score)
                                .map((player, index) => (
                                    <div key={player.id} className="flex justify-between py-2 border-b border-black/10 text-black/90">
                                        <span>{`${index + 1}. ${player.name}`}</span>
                                    </div>
                                ))
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

export default ColyseusMultiplayerPage;




